<template>
  <div>
    <v-dialog :value="value" max-width="620" persistent @input="$emit('input', $event)">
      <v-card>
      <v-card-title>Top 3 classroom report</v-card-title>
      <v-card-text>
        <p>Select a department, academic year, and term. The report includes the first three positions from its {{ source === 'virtual' ? 'virtual ' : '' }}classrooms with saved report cards.</p>
        <v-select v-model="ClassId" :items="departments" item-text="name" item-value="id" label="Department" outlined dense :loading="loadingDepartments" @change="departmentChanged" />
        <v-select v-model="YearId" :items="years" item-text="name" item-value="id" label="Academic year" outlined dense :disabled="!ClassId" :loading="loadingYears" @change="loadTerms" />
        <v-select v-model="TermId" :items="terms" item-text="name" item-value="id" label="Term" outlined dense :disabled="!YearId" :loading="loadingTerms" />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="loading" @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" :disabled="!ClassId || !YearId || !TermId" @click="createReport">
          Create report
        </v-btn>
      </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPdf" fullscreen hide-overlay transition="dialog-bottom-transition" content-class="report-preview-dialog">
      <v-card class="report-preview">
        <header class="report-preview__header">
          <div class="report-preview__identity">
            <span class="report-preview__icon"><v-icon color="primary">mdi-file-pdf-box</v-icon></span>
            <div class="report-preview__heading">
              <span>Rank report</span>
              <h2>Top 3 classroom rankings</h2>
              <p>{{ previewContext }}</p>
            </div>
          </div>
          <v-btn icon class="report-preview__close" aria-label="Close rank report preview" @click="closePreview">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>
        <main class="report-preview__document">
          <embed :src="pdfUrl" type="application/pdf" title="Top 3 classroom rank report PDF" />
        </main>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import pdfMake from "pdfmake/build/pdfmake.min";
import { pdfMake as pdfFonts } from "pdfmake/build/vfs_fonts";
import yearService from "@/modules/ReportCard/Year/service.js";
import termService from "@/modules/ReportCard/Terms/service.js";
import classRoomService from "@/modules/ClassRoom/service.js";
import virtualClassRoomService from "@/modules/VirtualClassRoom/service.js";
import classService from "@/modules/Classes/service.js";

pdfMake.vfs = pdfFonts.vfs;

export default {
  name: "classroom-rank-report",
  props: {
    value: { type: Boolean, default: false },
    source: { type: String, default: "regular" },
  },
  data: () => ({
    departments: [], years: [], terms: [], ClassId: null, YearId: null, TermId: null,
    loading: false, loadingDepartments: false, loadingYears: false, loadingTerms: false,
    showPdf: false, pdfUrl: null, previewContext: "",
  }),
  watch: {
    value(open) { if (open) this.loadOptions(); },
  },
  beforeDestroy() {
    this.clearPdfUrl();
  },
  methods: {
    close() { this.$emit("input", false); },
    resetSelection() {
      this.ClassId = null;
      this.YearId = null;
      this.TermId = null;
      this.years = [];
      this.terms = [];
    },
    clearPdfUrl() {
      if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl);
      this.pdfUrl = null;
    },
    closePreview() {
      this.showPdf = false;
      this.clearPdfUrl();
    },
    async loadOptions() {
      if (!this.departments.length) await this.loadDepartments();
    },
    async loadDepartments() {
      this.loadingDepartments = true;
      try {
        const response = await classService.getAll({ limit: 500 });
        this.departments = response.data || [];
      } finally { this.loadingDepartments = false; }
    },
    async loadYears() {
      this.YearId = null;
      this.TermId = null;
      this.years = [];
      this.terms = [];
      if (!this.ClassId) return;
      this.loadingYears = true;
      try {
        const response = await yearService.getAll({ ClassId: this.ClassId, limit: 100 });
        this.years = response.data || [];
      } finally { this.loadingYears = false; }
    },
    async loadTerms() {
      this.TermId = null;
      this.terms = [];
      if (!this.YearId) return;
      this.loadingTerms = true;
      try {
        const response = await termService.getAll({ YearId: this.YearId, limit: 100 });
        this.terms = response.data || [];
      } finally { this.loadingTerms = false; }
    },
    async departmentChanged() {
      await this.loadYears();
    },
    ordinal(rank) {
      if (rank === 1) return "1st";
      if (rank === 2) return "2nd";
      return "3rd";
    },
    buildDefinition(groups) {
      const year = this.years.find(item => Number(item.id) === Number(this.YearId));
      const term = this.terms.find(item => Number(item.id) === Number(this.TermId));
      const department = this.departments.find(item => Number(item.id) === Number(this.ClassId));
      const content = [
        { text: "TOP 3 CLASSROOM RANK REPORT", style: "title" },
        { text: `${department ? department.name : ""} · ${year ? year.name : ""} · ${term ? term.name : ""}`, style: "subtitle" },
      ];
      groups.forEach(group => {
        content.push({ text: group.classroomName, style: "classroom", margin: [0, 14, 0, 2] });
        content.push({ text: group.curriculumName, color: "#666666", margin: [0, 0, 0, 5] });
        content.push({
          table: {
            headerRows: 1,
            widths: [55, "*", 90],
            body: [
              [{ text: "Position", bold: true }, { text: "Student", bold: true }, { text: "Score", bold: true }],
              ...group.students.map(student => [this.ordinal(Number(student.rank)), student.studentName || "Student", `${Number(student.percentage).toFixed(2)}%`]),
            ],
          },
          layout: "lightHorizontalLines",
        });
      });
      return {
        pageSize: "LETTER", pageMargins: [40, 40, 40, 40], content,
        styles: { title: { fontSize: 18, bold: true, alignment: "center" }, subtitle: { fontSize: 11, alignment: "center", color: "#555555", margin: [0, 5, 0, 12] }, classroom: { fontSize: 13, bold: true } },
      };
    },
    async createReport() {
      this.loading = true;
      try {
        const service = this.source === "virtual" ? virtualClassRoomService : classRoomService;
        const response = await service.getRankReport({ ClassId: this.ClassId, YearId: this.YearId, TermId: this.TermId });
        const groups = Array.isArray(response) ? response : response.data || [];
        if (!groups.length) throw new Error("No saved rankings were found for this year and term.");
        const blob = await new Promise((resolve, reject) => {
          try { pdfMake.createPdf(this.buildDefinition(groups)).getBlob(resolve); }
          catch (pdfError) { reject(pdfError); }
        });
        const department = this.departments.find(item => Number(item.id) === Number(this.ClassId));
        const year = this.years.find(item => Number(item.id) === Number(this.YearId));
        const term = this.terms.find(item => Number(item.id) === Number(this.TermId));
        this.clearPdfUrl();
        this.pdfUrl = URL.createObjectURL(blob);
        this.previewContext = [department?.name, year?.name, term?.name].filter(Boolean).join(" · ");
        this.resetSelection();
        this.close();
        this.showPdf = true;
      } catch (error) {
        this.$swal({ icon: "info", title: "Rank report unavailable", text: error?.response?.data?.message || error.message || "Unable to create the rank report." });
      } finally { this.loading = false; }
    },
  },
};
</script>
